import type { SecFilingItem } from "../../../../../../../api/services/financial-integration.type";
import { Roles } from "../../../../../../../type";

export interface FilingData {
  key: string;
  document: string;
  fillingDate: string;
  fillingDatePrice: string;
  documentDate: string;
  link?: string;
}

export const formatDate = (dateString: string): string => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "N/A";
  }
};

export const formatPriceWithChange = (
  price: number | undefined | null,
  changePercent: number | undefined | null
): string => {
  const safePrice = price ?? 0;
  const safeChangePercent = changePercent ?? 0;

  const formattedPrice = `$${safePrice?.toFixed(2)}`;
  const sign = safeChangePercent >= 0 ? "+" : "";
  const formattedChange = `${sign}${safeChangePercent?.toFixed(2)}%`;
  return `${formattedPrice} (${formattedChange})`;
};

export const transformSecFilingData = (data: SecFilingItem[]): FilingData[] => {
  return data.map((item, index) => ({
    key: `${item.cik}-${item.filingDate}-${index}`,
    document: item.formType || "N/A",
    fillingDate: formatDate(item.filingDate),
    fillingDatePrice: formatPriceWithChange(
      item.filingDatePrice,
      item.changePercent
    ),
    documentDate: formatDate(item.acceptedDate),
    link: item.finalLink || item.link,
  }));
};

export const getSignalTagColor = (signalRaw?: string) => {
  if (!signalRaw) return "default";
  const signal = signalRaw.trim().toLowerCase();
  if (signal.startsWith("strong-buy")) return "blue";
  if (signal.startsWith("buy")) return "success";
  /** Yellow / gold chip — visible on dark backgrounds (avoid invalid "gray" preset) */
  if (signal.startsWith("hold")) return "gold";
  if (signal.startsWith("sell")) return "orange";
  if (signal.startsWith("strong-sell")) return "error";
  return "default";
};

export const showDataMonipulationButtonsByRole = (role: string) => {
  switch (role) {
    case Roles.SUPER_ADMIN:
    case Roles.ADMIN:
    case Roles.ANALITIK:
    case Roles.EDITOR:
      return true;
    default:
      return false;
  }
};

// Helper function to format numbers like CashFlowTable (B, M, K format)
export const formatNumberWithSuffix = (
  value: number | null | undefined
): string => {
  if (value === null || value === undefined) return "—";
  if (value === 0) return "0";

  const absValue = Math.abs(value);
  const sign = value < 0 ? "-" : "";

  if (absValue >= 1e12) {
    return `${sign}${(absValue / 1e12)?.toFixed(2)}T`;
  }
  if (absValue >= 1e9) {
    return `${sign}${(absValue / 1e9)?.toFixed(2)}B`;
  }
  if (absValue >= 1e6) {
    return `${sign}${(absValue / 1e6)?.toFixed(2)}M`;
  }
  if (absValue >= 1e3) {
    return `${sign}${(absValue / 1e3)?.toFixed(2)}K`;
  }
  return `${sign}${absValue?.toFixed(2)}`;
};
